import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { AuthField, AuthShell, ErrorNote, SubmitButton } from "@/components/AuthShell";
import { api, formatApiError } from "@/lib/api";

export default function RequestAccess() {
  const [form, setForm] = useState({ name: "", email: "", organization: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/access-requests", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell eyebrow="Request received" title="Thanks. We'll be in touch.">
        <div data-testid="request-access-success" className="space-y-6">
          <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-sm text-emerald-200">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <p>Your request has been sent. If it's approved, you'll get an email at <strong>{form.email}</strong> with a link to create your account.</p>
          </div>
          <Link to="/" data-testid="request-access-home-link" className="inline-block text-sm font-semibold text-white underline-offset-4 hover:underline">Back to home</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="Invitation only" title="Request an invitation">
      <form onSubmit={submit} className="space-y-5" data-testid="request-access-form">
        <p className="text-sm leading-relaxed text-slate-400">Know Her Name is a members-only database for coaches, scouts and media. Tell us who you are and we'll review your request.</p>
        <AuthField label="Full name" value={form.name} onChange={set("name")} required maxLength={120} data-testid="request-name-input" />
        <AuthField label="Email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid="request-email-input" />
        <AuthField label="Organization" value={form.organization} onChange={set("organization")} required maxLength={160}
          placeholder="Team, school, club or outlet" data-testid="request-organization-input" />
        <ErrorNote text={error} />
        <SubmitButton busy={busy} testId="request-access-submit-button">Send request</SubmitButton>
        <p className="text-center text-sm text-slate-400">
          Already a member?{" "}
          <Link to="/login" data-testid="request-access-login-link" className="font-semibold text-white underline-offset-4 hover:underline">Log in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
